/**
 * useCopilotSpatialNav — spatial keyboard navigation for the copilot view.
 *
 * Arrow keys move focus to the nearest focusable element in that direction,
 * ACROSS sections (lineup ↔ caption ↔ stats ↔ tabs), not just within a grid.
 * Runs in the capture phase so it wins over the per-grid/lineup arrow handlers,
 * and only acts when focus is already on a navigable element (so typing in
 * inputs etc. is unaffected).
 */
import { useEffect } from "react";

// The navigable elements across copilot sections.
const NAV_SELECTOR = '[data-stat-btn],[data-action-btn],[data-player-row="true"],[role="tab"]';

export function useCopilotSpatialNav(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;

    const onKey = (e) => {
      if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
      const active = document.activeElement;
      if (!active || typeof active.matches !== "function" || !active.matches(NAV_SELECTOR)) return;

      const r0 = active.getBoundingClientRect();
      const x0 = r0.left + r0.width / 2;
      const y0 = r0.top + r0.height / 2;
      let best = null;
      let bestScore = Infinity;

      document.querySelectorAll(NAV_SELECTOR).forEach((el) => {
        if (el === active || !el.getClientRects().length) return;
        const r = el.getBoundingClientRect();
        const dx = r.left + r.width / 2 - x0;
        const dy = r.top + r.height / 2 - y0;
        let inDir = false;
        let primary = 0;
        let secondary = 0;
        if (e.key === "ArrowUp") { inDir = dy < -2; primary = -dy; secondary = Math.abs(dx); }
        else if (e.key === "ArrowDown") { inDir = dy > 2; primary = dy; secondary = Math.abs(dx); }
        else if (e.key === "ArrowLeft") { inDir = dx < -2; primary = -dx; secondary = Math.abs(dy); }
        else { inDir = dx > 2; primary = dx; secondary = Math.abs(dy); }
        if (!inDir) return;
        const score = primary + secondary * 2; // favour staying in the same row/column
        if (score < bestScore) { bestScore = score; best = el; }
      });

      if (best) {
        e.preventDefault();
        e.stopPropagation();
        best.focus();
      }
    };

    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [enabled]);
}
